import './style.css';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { characterList, characterRegistry, type CharacterId, type RegisteredCharacter } from './characters/registry';
import type { CharacterShading, CharacterTextureSet, GlowProfile, LightingProfile, MaterialMatchRule } from './characters/types';
import { ReferenceObjectManager, referenceObjectLabels, referenceObjectLimits, type ReferenceObjectData, type ReferenceObjectId, type ReferenceObjectParameters, type ReferenceObjectType } from './referenceObjects';
import { setupHelpCenter } from './help/helpCenter';
import {
  bindPoseRig,
  getPoseOffsetEuler,
  getPoseOffsetQuaternion,
  poseJointDefinitions,
  resetPoseJoint,
  resetPoseRig,
  setPoseOffsetEuler,
  setPoseOffsetQuaternion,
  syncPoseJointCopies,
  type PoseJointId,
  type PoseRig,
} from './pose';

type ManagedLight = {
  id: string; name: string; kind: 'point' | 'directional'; light: THREE.PointLight | THREE.DirectionalLight;
  helper: THREE.PointLightHelper | THREE.DirectionalLightHelper; enabled: boolean; baseIntensity: number;
  directionRotation?: THREE.Euler; defaultDirectionRotation?: THREE.Euler; directionDistance?: number;
};

const characterLook = {
  surfaceEmission:.10,
  eyeEmission:.48,
  hairSaturation:1.18,
  bodySaturation:1.14,
  faceSaturation:1.08,
  eyeSaturation:1.22,
} as const;
const renderSettings = {
  saturationScale:1,
  surfaceEmission:characterLook.surfaceEmission,
  eyeEmission:characterLook.eyeEmission,
  faceBrightness:1.15,
  rampCenter:.145,
  rampSoftness:.08,
  outlineScale:.65,
  bloomStrength:.30,
  faceRimStrength:.55,
};
const DEFAULT_CHARACTER_ID:CharacterId = 'mizuki';
const DEFAULT_BACKGROUND = '#454a54';
const DEFAULT_FOG = '#353a43';
const defaultRenderSettings = { ...renderSettings };
type ReferenceRenderMode = 'full' | 'clay';
type CharacterMaterialBinding = {
  full:THREE.Material | THREE.Material[];
  clay:THREE.Material | THREE.Material[];
};
let referenceRenderMode:ReferenceRenderMode = 'full';
let usesDefaultBackground = true;
let currentBackgroundColor = DEFAULT_BACKGROUND;

const $ = <T extends HTMLElement>(selector: string) => document.querySelector<T>(selector)!;
function createDefaultBackgroundTexture() {
  const background = document.createElement('canvas');
  background.width = 768;
  background.height = 768;
  const context = background.getContext('2d')!;
  const vertical = context.createLinearGradient(0, 0, background.width, background.height);
  vertical.addColorStop(0, '#656872');
  vertical.addColorStop(.48, '#474c57');
  vertical.addColorStop(1, '#292e36');
  context.fillStyle = vertical;
  context.fillRect(0, 0, background.width, background.height);
  const coolGlow = context.createRadialGradient(140, 155, 0, 140, 155, 500);
  coolGlow.addColorStop(0, 'rgba(112, 130, 147, .26)');
  coolGlow.addColorStop(1, 'rgba(112, 130, 147, 0)');
  context.fillStyle = coolGlow;
  context.fillRect(0, 0, background.width, background.height);
  const plumShadow = context.createRadialGradient(690, 690, 0, 690, 690, 460);
  plumShadow.addColorStop(0, 'rgba(93, 78, 103, .20)');
  plumShadow.addColorStop(1, 'rgba(93, 78, 103, 0)');
  context.fillStyle = plumShadow;
  context.fillRect(0, 0, background.width, background.height);
  const texture = new THREE.CanvasTexture(background);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
}

const defaultBackgroundTexture = createDefaultBackgroundTexture();
const canvas = $('#viewport') as HTMLCanvasElement;
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, preserveDrawingBuffer: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setSize(innerWidth, innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = .98;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

const scene = new THREE.Scene();
scene.background = defaultBackgroundTexture;
scene.fog = new THREE.Fog(DEFAULT_FOG, 16, 32);
const camera = new THREE.PerspectiveCamera(35, innerWidth / innerHeight, 0.05, 100);
camera.position.set(4.4, 3.15, 7.6);
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true; controls.dampingFactor = .06; controls.minDistance = 2.2; controls.maxDistance = 15;
controls.target.set(0, 1.75, 0);
const composer = new EffectComposer(renderer);
composer.setPixelRatio(Math.min(devicePixelRatio, 2));
composer.setSize(innerWidth, innerHeight);
composer.addPass(new RenderPass(scene, camera));
const bloomPass = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), .30, .28, 1.05);
composer.addPass(bloomPass);
composer.addPass(new OutputPass());

const transform = new TransformControls(camera, renderer.domElement);
transform.setMode('translate'); transform.setSize(.75);
transform.addEventListener('dragging-changed', (e) => {
  controls.enabled = !e.value;
  if (e.value) recordContinuousChange(selectedPoseJoint ? '调整关节' : selectedReferenceId ? '变换参考物' : '移动灯光');
  else endContinuousChange();
});
transform.addEventListener('objectChange', () => {
  if (selected?.light instanceof THREE.DirectionalLight) {
    syncDirectionalRotationFromPosition(selected);
    syncDirectionalRotationEditor(selected);
  }
  selected?.helper.update();
  if (selectedPoseJoint) {
    const binding = poseRig.get(selectedPoseJoint);
    if (binding) syncPoseJointCopies(binding);
    syncPoseRotationUi();
  }
  if (selectedReferenceId) syncReferenceEditor();
});
scene.add(transform.getHelper());

const ambient = new THREE.HemisphereLight(0xdce7ff, 0x584957, .72); scene.add(ambient);
const ground = new THREE.Mesh(new THREE.CircleGeometry(5.8, 80), new THREE.MeshStandardMaterial({ color:0x15181d, roughness:.82, metalness:.04 }));
ground.rotation.x = -Math.PI/2; ground.position.y = -.015; ground.receiveShadow = true; scene.add(ground);
const grid = new THREE.GridHelper(12, 24, 0x353c49, 0x242a34); grid.position.y = .005; grid.visible = false; (grid.material as THREE.Material).transparent = true; (grid.material as THREE.Material).opacity = .42; scene.add(grid);

const lightGroup = new THREE.Group(); scene.add(lightGroup);
const referenceGroup = new THREE.Group(); referenceGroup.name = 'ReferenceObjects'; scene.add(referenceGroup);
const referenceManager = new ReferenceObjectManager(referenceGroup, referenceObjectLimits);
const lights: ManagedLight[] = []; let selected: ManagedLight | null = null; let lightIndex = 0; let character: THREE.Group | null = null;
let poseRig:PoseRig = new Map();
let poseModeEnabled = false;
let selectedPoseJoint:PoseJointId | null = null;
let selectedReferenceId:ReferenceObjectId | null = null;
let activeCharacterId: CharacterId = DEFAULT_CHARACTER_ID;
let characterLoadToken = 0;
let sharedPoseApplied = false;
const textureLoader = new THREE.TextureLoader();
const textureCache = new Map<string, THREE.Texture>();
function getCharacterShading(characterId:CharacterId):CharacterShading {
  return characterRegistry[characterId].shading;
}
function colorFromTuple([r, g, b]:readonly [number, number, number]) {
  return new THREE.Color(r, g, b);
}
type TextureKey = keyof CharacterTextureSet;
type MaterialProfile = {
  diffuse:TextureKey;
  lighting?:LightingProfile;
  glow?:GlowProfile;
  maskedEyeGlow?:boolean;
  faceCheekRim?:boolean;
};
type FaceLightUniformSet = {
  direction:{ value:THREE.Vector3 };
  directColor:{ value:THREE.Color };
  ambientColor:{ value:THREE.Color };
  emission:{ value:number };
  brightness:{ value:number };
  saturation:{ value:number };
  eyeGlow:{ value:number };
  softness:{ value:number };
  rimStrength:{ value:number };
  baseSaturation:number;
  maskedEyeGlow:boolean;
};
type ToonUniformSet = {
  saturation:{ value:number };
  rampCenter:{ value:number };
  rampSoftness:{ value:number };
  baseSaturation:number;
};
type EyeGradeUniformSet = { saturation:{ value:number }; baseSaturation:number };
type OutlineUniformSet = { width:{ value:number }; baseWidth:number };
const faceLightUniforms:FaceLightUniformSet[] = [];
const toonUniforms:ToonUniformSet[] = [];
const eyeGradeUniforms:EyeGradeUniformSet[] = [];
const outlineUniforms:OutlineUniformSet[] = [];
const subtleGlowMaterials = new Set<THREE.MeshPhongMaterial>();
const eyeGlowMaterials = new Set<THREE.MeshPhongMaterial>();

function clearRenderBindings() {
  faceLightUniforms.length = 0;
  toonUniforms.length = 0;
  eyeGradeUniforms.length = 0;
  outlineUniforms.length = 0;
  subtleGlowMaterials.clear();
  eyeGlowMaterials.clear();
}

function applyRenderSettings() {
  toonUniforms.forEach((uniforms) => {
    uniforms.saturation.value = uniforms.baseSaturation * renderSettings.saturationScale;
    uniforms.rampCenter.value = renderSettings.rampCenter;
    uniforms.rampSoftness.value = renderSettings.rampSoftness;
  });
  faceLightUniforms.forEach((uniforms) => {
    uniforms.emission.value = renderSettings.surfaceEmission;
    uniforms.brightness.value = renderSettings.faceBrightness;
    uniforms.saturation.value = uniforms.baseSaturation * renderSettings.saturationScale;
    uniforms.eyeGlow.value = uniforms.maskedEyeGlow
      ? Math.max(0, renderSettings.eyeEmission - renderSettings.surfaceEmission)
      : 0;
    uniforms.rimStrength.value = renderSettings.faceRimStrength;
  });
  eyeGradeUniforms.forEach((uniforms) => {
    uniforms.saturation.value = uniforms.baseSaturation * renderSettings.saturationScale;
  });
  outlineUniforms.forEach((uniforms) => {
    const modeScale = referenceRenderMode === 'clay' ? .65 : 1;
    uniforms.width.value = uniforms.baseWidth * renderSettings.outlineScale * modeScale;
  });
  subtleGlowMaterials.forEach((material) => {
    material.emissiveIntensity = renderSettings.surfaceEmission;
  });
  eyeGlowMaterials.forEach((material) => {
    material.emissiveIntensity = renderSettings.eyeEmission;
  });
  bloomPass.strength = referenceRenderMode === 'clay' ? 0 : renderSettings.bloomStrength;
}

function getTexture(characterId:CharacterId, key:TextureKey, colorTexture = false) {
  const textures:CharacterTextureSet = characterRegistry[characterId].textures;
  const url = textures[key];
  if (!url) throw new Error(`角色 ${characterId} 缺少贴图配置：${key}`);
  if (!textureCache.has(url)) {
    const texture = textureLoader.load(url);
    if (colorTexture) texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = true;
    if (key.endsWith('Ramp')) {
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
    }
    texture.anisotropy = renderer.capabilities.getMaxAnisotropy(); textureCache.set(url, texture);
  }
  return textureCache.get(url)!;
}
function materialProfile(characterId:CharacterId, materialName:string, meshName:string): MaterialProfile | null {
  const rules:readonly MaterialMatchRule[] = characterRegistry[characterId].materialRules;
  const rule = rules.find((candidate) =>
    (!candidate.mesh || candidate.mesh === meshName)
    && (!candidate.meshPattern || candidate.meshPattern.test(meshName))
    && (!candidate.materialPattern || candidate.materialPattern.test(materialName)),
  );
  if (!rule) return null;
  return {
    diffuse:rule.diffuse as TextureKey,
    lighting:rule.lighting,
    glow:rule.glow,
    maskedEyeGlow:rule.maskedEyeGlow && meshName === 'Face_Eye',
    faceCheekRim:rule.faceCheekRim,
  };
}
function applyHairLighting(characterId:CharacterId, material:THREE.MeshPhongMaterial, lightmapKey:TextureKey, rampKey:TextureKey, cacheKey:string) {
  const lightmap = getTexture(characterId, lightmapKey);
  const ramp = getTexture(characterId, rampKey, true);
  const toonBinding:ToonUniformSet = {
    saturation:{ value:characterLook.hairSaturation * renderSettings.saturationScale },
    rampCenter:{ value:renderSettings.rampCenter },
    rampSoftness:{ value:renderSettings.rampSoftness },
    baseSaturation:characterLook.hairSaturation,
  };
  toonUniforms.push(toonBinding);
  material.emissive.set(0x000000);
  material.emissiveIntensity = 0;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.odetteHairLightmap = { value:lightmap };
    shader.uniforms.odetteHairRamp = { value:ramp };
    shader.uniforms.odetteHairShadowTint = {
      value:colorFromTuple(getCharacterShading(characterId).hairShadowTint),
    };
    shader.uniforms.odetteHairLightStrength = { value:.76 };
    shader.uniforms.odetteHairSaturation = toonBinding.saturation;
    shader.uniforms.odetteHairRampCenter = toonBinding.rampCenter;
    shader.uniforms.odetteHairRampSoftness = toonBinding.rampSoftness;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_pars_fragment>',
        `#include <map_pars_fragment>
uniform sampler2D odetteHairLightmap;
uniform sampler2D odetteHairRamp;
uniform vec3 odetteHairShadowTint;
uniform float odetteHairLightStrength;
uniform float odetteHairSaturation;
uniform float odetteHairRampCenter;
uniform float odetteHairRampSoftness;`,
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
vec4 odetteHairMask = texture2D( odetteHairLightmap, vMapUv );
float odetteHairWeight = smoothstep( 0.10, 0.90, odetteHairMask.g );
float odetteHairRampRow = clamp( odetteHairMask.a, 0.025, 0.975 );
vec3 odetteHairLitTone = vec3( 1.02 );
vec3 odetteHairTone = mix( odetteHairShadowTint, odetteHairLitTone, odetteHairWeight );
diffuseColor.rgb *= mix( vec3( 1.0 ), odetteHairTone, odetteHairLightStrength );
float odetteHairLuma = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
diffuseColor.rgb = max( vec3( 0.0 ), mix( vec3( odetteHairLuma ), diffuseColor.rgb, odetteHairSaturation ) );`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
float odetteHairEmissionLuma = dot( totalEmissiveRadiance, vec3( 0.2126, 0.7152, 0.0722 ) );
totalEmissiveRadiance = max(
  vec3( 0.0 ),
  mix( vec3( odetteHairEmissionLuma ), totalEmissiveRadiance, odetteHairSaturation )
);`,
      )
      .replace(
        '#include <lights_fragment_end>',
        `#include <lights_fragment_end>
vec3 odetteHairDirectLighting = reflectedLight.directDiffuse
  / max( diffuseColor.rgb, vec3( 0.025 ) );
float odetteHairDirectLevel = dot(
  odetteHairDirectLighting,
  vec3( 0.2126, 0.7152, 0.0722 )
);
float odetteHairToonBand = smoothstep(
  odetteHairRampCenter - odetteHairRampSoftness * 0.5,
  odetteHairRampCenter + odetteHairRampSoftness * 0.5,
  odetteHairDirectLevel
);
float odetteHairRampCoord = smoothstep(
  0.08,
  0.92,
  mix( 0.12, 0.88, odetteHairToonBand )
);
vec3 odetteHairDynamicRamp = texture2D(
  odetteHairRamp,
  vec2( odetteHairRampCoord, odetteHairRampRow )
).rgb;
vec3 odetteHairDirectTint = odetteHairDirectLighting
  / max( odetteHairDirectLevel, 0.001 );
float odetteHairToonEnergy = mix(
  0.20,
  clamp( odetteHairDirectLevel, 0.78, 1.18 ),
  odetteHairToonBand
);
odetteHairToonEnergy *= step( 0.001, odetteHairDirectLevel );
reflectedLight.directDiffuse = diffuseColor.rgb
  * odetteHairDirectTint
  * odetteHairToonEnergy
  * mix( vec3( 1.0 ), odetteHairDynamicRamp, 0.60 );
float odetteHairSpecularMask = smoothstep( 0.12, 0.82, odetteHairMask.r );
float odetteHairSpecularLuma = dot(
  reflectedLight.directSpecular,
  vec3( 0.2126, 0.7152, 0.0722 )
);
float odetteHairSpecularBand = smoothstep( 0.025, 0.085, odetteHairSpecularLuma );
reflectedLight.directSpecular *= odetteHairSpecularBand
  * mix( 0.08, 1.10, odetteHairSpecularMask );`,
      );
  };
  material.customProgramCacheKey = () => `${cacheKey}-hair-lightmap-v5-dynamic-ramp`;
}
function applyBodyLighting(characterId:CharacterId, material:THREE.MeshPhongMaterial, lightmapKey:TextureKey, rampKey:TextureKey, cacheKey:string) {
  const lightmap = getTexture(characterId, lightmapKey);
  const ramp = getTexture(characterId, rampKey, true);
  const toonBinding:ToonUniformSet = {
    saturation:{ value:characterLook.bodySaturation * renderSettings.saturationScale },
    rampCenter:{ value:renderSettings.rampCenter },
    rampSoftness:{ value:renderSettings.rampSoftness },
    baseSaturation:characterLook.bodySaturation,
  };
  toonUniforms.push(toonBinding);
  material.onBeforeCompile = (shader) => {
    const shading = getCharacterShading(characterId);
    const bodyShadowTint = lightmapKey === 'body01Lightmap' && shading.body01ShadowTint
      ? shading.body01ShadowTint
      : shading.bodyShadowTint;
    shader.uniforms.odetteBodyLightmap = { value:lightmap };
    shader.uniforms.odetteBodyRamp = { value:ramp };
    shader.uniforms.odetteBodyShadowTint = {
      value:colorFromTuple(bodyShadowTint),
    };
    shader.uniforms.odetteBodyControlStrength = { value:.62 };
    shader.uniforms.odetteBodySaturation = toonBinding.saturation;
    shader.uniforms.odetteBodyRampCenter = toonBinding.rampCenter;
    shader.uniforms.odetteBodyRampSoftness = toonBinding.rampSoftness;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_pars_fragment>',
        `#include <map_pars_fragment>
uniform sampler2D odetteBodyLightmap;
uniform sampler2D odetteBodyRamp;
uniform vec3 odetteBodyShadowTint;
uniform float odetteBodyControlStrength;
uniform float odetteBodySaturation;
uniform float odetteBodyRampCenter;
uniform float odetteBodyRampSoftness;`,
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
vec4 odetteBodyControl = texture2D( odetteBodyLightmap, vMapUv );
float odetteBodyWeight = smoothstep( 0.10, 0.90, odetteBodyControl.g );
float odetteBodyRampRow = clamp( odetteBodyControl.a, 0.025, 0.975 );
vec3 odetteBodyTone = mix( odetteBodyShadowTint, vec3( 1.02 ), odetteBodyWeight );
diffuseColor.rgb *= mix( vec3( 1.0 ), odetteBodyTone, odetteBodyControlStrength );
float odetteBodyLuma = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
diffuseColor.rgb = max( vec3( 0.0 ), mix( vec3( odetteBodyLuma ), diffuseColor.rgb, odetteBodySaturation ) );`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
float odetteBodyEmissionLuma = dot( totalEmissiveRadiance, vec3( 0.2126, 0.7152, 0.0722 ) );
totalEmissiveRadiance = max(
  vec3( 0.0 ),
  mix( vec3( odetteBodyEmissionLuma ), totalEmissiveRadiance, odetteBodySaturation )
);`,
      )
      .replace(
        '#include <lights_fragment_end>',
        `#include <lights_fragment_end>
vec3 odetteBodyDirectLighting = reflectedLight.directDiffuse
  / max( diffuseColor.rgb, vec3( 0.025 ) );
float odetteBodyDirectLevel = dot(
  odetteBodyDirectLighting,
  vec3( 0.2126, 0.7152, 0.0722 )
);
float odetteBodyToonBand = smoothstep(
  odetteBodyRampCenter - odetteBodyRampSoftness * 0.5,
  odetteBodyRampCenter + odetteBodyRampSoftness * 0.5,
  odetteBodyDirectLevel
);
float odetteBodyRampCoord = smoothstep(
  0.08,
  0.92,
  mix( 0.12, 0.88, odetteBodyToonBand )
);
vec3 odetteBodyDynamicRamp = texture2D(
  odetteBodyRamp,
  vec2( odetteBodyRampCoord, odetteBodyRampRow )
).rgb;
vec3 odetteBodyDirectTint = odetteBodyDirectLighting
  / max( odetteBodyDirectLevel, 0.001 );
float odetteBodyToonEnergy = mix(
  0.22,
  clamp( odetteBodyDirectLevel, 0.80, 1.20 ),
  odetteBodyToonBand
);
odetteBodyToonEnergy *= step( 0.001, odetteBodyDirectLevel );
reflectedLight.directDiffuse = diffuseColor.rgb
  * odetteBodyDirectTint
  * odetteBodyToonEnergy
  * mix( vec3( 1.0 ), odetteBodyDynamicRamp, 0.64 );
float odetteBodySpecularMask = smoothstep( 0.12, 0.82, odetteBodyControl.r );
float odetteBodySpecularLuma = dot(
  reflectedLight.directSpecular,
  vec3( 0.2126, 0.7152, 0.0722 )
);
float odetteBodySpecularBand = smoothstep( 0.025, 0.085, odetteBodySpecularLuma );
reflectedLight.directSpecular *= odetteBodySpecularBand
  * mix( 0.06, 1.10, odetteBodySpecularMask );`,
      );
  };
  material.customProgramCacheKey = () => `${cacheKey}-${lightmapKey}-ramp-v4-dynamic`;
}
function applyFaceSdf(
  characterId:CharacterId,
  material:THREE.MeshBasicMaterial,
  sdfKey:TextureKey,
  shadowKey:TextureKey,
  cacheKey:string,
  maskedEyeGlow = false,
  cheekRimEnabled = false,
) {
  const sdf = getTexture(characterId, sdfKey);
  const shadowMap = getTexture(characterId, shadowKey);
  const lightUniforms:FaceLightUniformSet = {
    direction:{ value:new THREE.Vector3(0, 0, 1) },
    directColor:{ value:new THREE.Color(0, 0, 0) },
    ambientColor:{ value:new THREE.Color(0, 0, 0) },
    emission:{ value:renderSettings.surfaceEmission },
    brightness:{ value:renderSettings.faceBrightness },
    saturation:{ value:characterLook.faceSaturation * renderSettings.saturationScale },
    eyeGlow:{
      value:maskedEyeGlow
        ? Math.max(0, renderSettings.eyeEmission - renderSettings.surfaceEmission)
        : 0,
    },
    softness:{ value:.020 },
    rimStrength:{ value:renderSettings.faceRimStrength },
    baseSaturation:characterLook.faceSaturation,
    maskedEyeGlow,
  };
  faceLightUniforms.push(lightUniforms);
  material.onBeforeCompile = (shader) => {
    shader.uniforms.odetteFaceSdf = { value:sdf };
    shader.uniforms.odetteFaceShadowMap = { value:shadowMap };
    shader.uniforms.odetteFaceLightDirection = lightUniforms.direction;
    shader.uniforms.odetteFaceDirectColor = lightUniforms.directColor;
    shader.uniforms.odetteFaceAmbientColor = lightUniforms.ambientColor;
    shader.uniforms.odetteFaceShadowColor = {
      value:colorFromTuple(getCharacterShading(characterId).faceShadowColor),
    };
    shader.uniforms.odetteFaceSoftness = lightUniforms.softness;
    shader.uniforms.odetteFaceEmission = lightUniforms.emission;
    shader.uniforms.odetteFaceBrightness = lightUniforms.brightness;
    shader.uniforms.odetteFaceSaturation = lightUniforms.saturation;
    shader.uniforms.odetteFaceCheekRimEnabled = { value:cheekRimEnabled ? 1 : 0 };
    shader.uniforms.odetteFaceRimStrength = lightUniforms.rimStrength;
    shader.uniforms.odetteFaceEyeGlow = lightUniforms.eyeGlow;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_pars_fragment>',
        `#include <map_pars_fragment>
uniform sampler2D odetteFaceSdf;
uniform sampler2D odetteFaceShadowMap;
uniform vec3 odetteFaceLightDirection;
uniform vec3 odetteFaceDirectColor;
uniform vec3 odetteFaceAmbientColor;
uniform vec3 odetteFaceShadowColor;
uniform float odetteFaceSoftness;
uniform float odetteFaceEmission;
uniform float odetteFaceBrightness;
uniform float odetteFaceSaturation;
uniform float odetteFaceCheekRimEnabled;
uniform float odetteFaceRimStrength;
uniform float odetteFaceEyeGlow;`,
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
vec3 odetteFaceBase = diffuseColor.rgb;
vec2 odetteFaceUv = vMapUv;
vec2 odetteFaceLightXZ = vec2( odetteFaceLightDirection.x, odetteFaceLightDirection.z );
odetteFaceLightXZ /= max( 0.001, length( odetteFaceLightXZ ) );
if ( odetteFaceLightXZ.x < 0.0 ) odetteFaceUv.x = 1.0 - odetteFaceUv.x;
float odetteFaceDistance = texture2D( odetteFaceSdf, odetteFaceUv ).r;
float odetteFaceThreshold = clamp( ( 1.0 - odetteFaceLightXZ.y ) * 0.5, 0.03, 0.97 );
float odetteFaceSdfLit = smoothstep(
  odetteFaceThreshold - odetteFaceSoftness,
  odetteFaceThreshold + odetteFaceSoftness,
  odetteFaceDistance
);
float odetteFaceFrontWeight = smoothstep( -0.62, 0.12, odetteFaceLightXZ.y );
float odetteFaceFrontLit = odetteFaceSdfLit * odetteFaceFrontWeight;
float odetteFaceBackAmount = smoothstep( 0.05, 0.90, -odetteFaceLightXZ.y );
float odetteFaceLateralAmount = smoothstep( 0.08, 0.42, abs( odetteFaceLightXZ.x ) );
float odetteFaceBilateralEdge = smoothstep(
  0.60,
  0.92,
  abs( vMapUv.x - 0.5 ) * 2.0
);
float odetteFaceOrientedEdge = smoothstep( 0.62, 0.90, odetteFaceUv.x );
float odetteFaceCheekEdge = mix(
  odetteFaceBilateralEdge,
  odetteFaceOrientedEdge,
  odetteFaceLateralAmount
);
float odetteFaceCheekHeight = smoothstep( 0.18, 0.34, vMapUv.y )
  * ( 1.0 - smoothstep( 0.66, 0.82, vMapUv.y ) );
float odetteFaceCheekRim = odetteFaceCheekEdge
  * odetteFaceCheekHeight
  * odetteFaceBackAmount
  * mix( 0.22, odetteFaceRimStrength, odetteFaceLateralAmount )
  * odetteFaceCheekRimEnabled;
float odetteFaceLit = max( odetteFaceFrontLit, odetteFaceCheekRim );
vec3 odetteFaceTone = mix( odetteFaceShadowColor, vec3( 1.0 ), odetteFaceLit );
float odetteFaceControl = texture2D( odetteFaceShadowMap, vMapUv ).b;
float odetteFaceAo = mix( 0.94, 1.0, odetteFaceControl );
float odetteFaceLuma = dot( odetteFaceBase, vec3( 0.2126, 0.7152, 0.0722 ) );
vec3 odetteFaceAlbedo = max(
  vec3( 0.0 ),
  mix( vec3( odetteFaceLuma ), odetteFaceBase, odetteFaceSaturation )
);
vec3 odetteFaceIrradiance = odetteFaceAmbientColor + odetteFaceDirectColor * odetteFaceLit;
vec3 odetteFaceDiffuse = odetteFaceAlbedo
  * odetteFaceIrradiance
  * RECIPROCAL_PI
  * odetteFaceTone
  * odetteFaceAo;
vec3 odetteFaceSurfaceEmission = odetteFaceAlbedo * odetteFaceEmission;
diffuseColor.rgb = ( odetteFaceDiffuse + odetteFaceSurfaceEmission ) * odetteFaceBrightness;
float odetteFaceMax = max( odetteFaceBase.r, max( odetteFaceBase.g, odetteFaceBase.b ) );
float odetteFacePurple = max( 0.0, min( odetteFaceBase.r, odetteFaceBase.b ) - odetteFaceBase.g );
float odetteFaceRedBlueBalance = 1.0 - abs( odetteFaceBase.r - odetteFaceBase.b ) / max( 0.001, odetteFaceMax );
float odetteFaceEyeMask = smoothstep( 0.055, 0.20, odetteFacePurple )
  * smoothstep( 0.32, 0.72, odetteFaceRedBlueBalance )
  * smoothstep( 0.12, 0.38, odetteFaceMax );
diffuseColor.rgb += odetteFaceBase * odetteFaceEyeMask * odetteFaceEyeGlow;`,
      );
  };
  material.customProgramCacheKey = () => `${cacheKey}-face-sdf-v8-brightness-${maskedEyeGlow ? 'eye' : 'base'}-${cheekRimEnabled ? 'rim' : 'plain'}`;
}
function applyEyeGrade(material:THREE.MeshPhongMaterial, cacheKey:string) {
  const eyeBinding:EyeGradeUniformSet = {
    saturation:{ value:characterLook.eyeSaturation * renderSettings.saturationScale },
    baseSaturation:characterLook.eyeSaturation,
  };
  eyeGradeUniforms.push(eyeBinding);
  material.onBeforeCompile = (shader) => {
    shader.uniforms.characterEyeSaturation = eyeBinding.saturation;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_pars_fragment>',
        `#include <map_pars_fragment>
uniform float characterEyeSaturation;`,
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
float characterEyeLuma = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
diffuseColor.rgb = max(
  vec3( 0.0 ),
  mix( vec3( characterEyeLuma ), diffuseColor.rgb, characterEyeSaturation )
);`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
float characterEyeEmissionLuma = dot( totalEmissiveRadiance, vec3( 0.2126, 0.7152, 0.0722 ) );
totalEmissiveRadiance = max(
  vec3( 0.0 ),
  mix( vec3( characterEyeEmissionLuma ), totalEmissiveRadiance, characterEyeSaturation )
);`,
      );
  };
  material.customProgramCacheKey = () => `${cacheKey}-eye-grade-v1`;
}
type OutlineStyle = { color:number; worldWidth:number };

function outlineStyle(characterId:CharacterId, profile:MaterialProfile | null):OutlineStyle | null {
  const outline = characterRegistry[characterId].outline;
  if (!outline.enabled) return null;
  if (profile?.lighting === 'hair') {
    return { color:outline.hairColor, worldWidth:outline.hairWidth };
  }
  if (profile?.lighting === 'body' || profile?.lighting === 'body01') {
    return { color:outline.bodyColor, worldWidth:outline.bodyWidth };
  }
  return null;
}

function createOutlineMaterial(
  characterId:CharacterId,
  profile:MaterialProfile | null,
  map:THREE.Texture | null,
  localWidth:number,
) {
  const style = outlineStyle(characterId, profile);
  if (!style) return new THREE.MeshBasicMaterial({ visible:false });
  const outlineBinding:OutlineUniformSet = {
    width:{ value:localWidth * renderSettings.outlineScale },
    baseWidth:localWidth,
  };
  outlineUniforms.push(outlineBinding);
  const material = new THREE.MeshBasicMaterial({
    name:`${characterId}-${profile!.lighting}-outline`,
    color:style.color,
    map,
    side:THREE.BackSide,
    alphaTest:profile!.lighting === 'hair' ? .5 : 0,
    alphaToCoverage:profile!.lighting === 'hair',
    depthTest:true,
    depthWrite:true,
    polygonOffset:true,
    polygonOffsetFactor:1,
    polygonOffsetUnits:1,
    toneMapped:true,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.characterOutlineWidth = outlineBinding.width;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float characterOutlineWidth;`,
      )
      .replace(
        '#include <begin_vertex>',
        `vec3 transformed = vec3( position );
transformed += normalize( normal ) * characterOutlineWidth;`,
      );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#ifdef USE_MAP
vec4 sampledDiffuseColor = texture2D( map, vMapUv );
diffuseColor.a *= sampledDiffuseColor.a;
#endif`,
    );
  };
  material.customProgramCacheKey = () => `${characterId}-${profile!.lighting}-outline-v1`;
  return material;
}
function rebuildMaterial(characterId:CharacterId, source: THREE.Material, meshName:string) {
  const profile = materialProfile(characterId, source.name || '', meshName);
  if (!profile) {
    console.warn(`[material] 已隐藏未使用槽位: ${meshName} / ${source.name || 'unnamed'}`);
    return new THREE.MeshPhongMaterial({ name:source.name, color:0x5c6370, visible:false });
  }
  if (profile.lighting === 'face') {
    const faceMaterial = new THREE.MeshBasicMaterial({
      name:source.name,
      map:getTexture(characterId, profile.diffuse, true),
      color:0xffffff,
      side:THREE.FrontSide,
    });
    applyFaceSdf(
      characterId,
      faceMaterial,
      'faceSdf',
      'faceShadow',
      characterId,
      profile.maskedEyeGlow,
      profile.faceCheekRim,
    );
    return faceMaterial;
  }
  const material = new THREE.MeshPhongMaterial({
    name:source.name,
    map:getTexture(characterId, profile.diffuse, true),
    color:0xffffff,
    specular:0xffffff,
    shininess:4,
    side:profile.lighting === 'hair' ? THREE.DoubleSide : THREE.FrontSide,
    transparent:profile.lighting === 'hair',
    alphaTest:profile.lighting === 'hair' ? .5 : 0,
    alphaToCoverage:profile.lighting === 'hair',
  });
  if (profile.lighting === 'hair') applyHairLighting(
    characterId,
    material,
    'hairLightmap',
    'hairRamp',
    characterId,
  );
  if (profile.lighting === 'body') applyBodyLighting(
    characterId,
    material,
    'bodyLightmap',
    'bodyRamp',
    characterId,
  );
  if (profile.lighting === 'body01') applyBodyLighting(characterId, material, 'body01Lightmap', 'body01Ramp', characterId);
  if (profile.glow === 'subtle') {
    material.emissive.set(0xffffff);
    material.emissiveMap = material.map;
    material.emissiveIntensity = renderSettings.surfaceEmission;
    subtleGlowMaterials.add(material);
  }
  if (profile.glow === 'eye') {
    material.emissive.set(0xa79cff);
    material.emissiveMap = material.map;
    material.emissiveIntensity = renderSettings.eyeEmission;
    eyeGlowMaterials.add(material);
    material.toneMapped = true;
    material.transparent = true;
    material.alphaTest = .76;
    material.alphaToCoverage = true;
    applyEyeGrade(material, characterId);
  }
  return material;
}

function createClayMaterial(source:THREE.Material, cacheKey:string) {
  if (!source.visible) return new THREE.MeshPhongMaterial({ name:`${source.name}-clay-hidden`, visible:false });
  const sourceMap = source instanceof THREE.MeshBasicMaterial || source instanceof THREE.MeshPhongMaterial
    ? source.map
    : null;
  const material = new THREE.MeshPhongMaterial({
    name:`${source.name}-clay`,
    color:0x8f949b,
    map:sourceMap,
    specular:0x111111,
    shininess:2,
    emissive:0x000000,
    emissiveIntensity:0,
    side:source.side,
    transparent:source.transparent,
    opacity:source.opacity,
    alphaTest:source.alphaTest,
    alphaToCoverage:source.alphaToCoverage,
    depthTest:source.depthTest,
    depthWrite:source.depthWrite,
    toneMapped:true,
  });
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
diffuseColor.rgb = vec3( 0.56, 0.58, 0.61 );`,
      )
      .replace(
        'vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;',
        `vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;
float characterClayLuma = dot( outgoingLight, vec3( 0.2126, 0.7152, 0.0722 ) );
outgoingLight = vec3( characterClayLuma );`,
      );
  };
  material.customProgramCacheKey = () => `${cacheKey}-clay-v1`;
  return material;
}

function createClayMaterialSet(full:THREE.Material | THREE.Material[], cacheKey:string) {
  return Array.isArray(full)
    ? full.map((material, index) => createClayMaterial(material, `${cacheKey}-${index}`))
    : createClayMaterial(full, cacheKey);
}

function materialList(material:THREE.Material | THREE.Material[]) {
  return Array.isArray(material) ? material : [material];
}

function disposeCharacter(root:THREE.Group) {
  const disposedGeometries = new Set<THREE.BufferGeometry>();
  const disposedMaterials = new Set<THREE.Material>();
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    if (!disposedGeometries.has(node.geometry)) {
      disposedGeometries.add(node.geometry);
      node.geometry.dispose();
    }
    const binding = node.userData.referenceMaterials as CharacterMaterialBinding | undefined;
    const materials = binding
      ? [...materialList(binding.full), ...materialList(binding.clay)]
      : materialList(node.material);
    materials.forEach((material) => {
      if (disposedMaterials.has(material)) return;
      disposedMaterials.add(material);
      material.dispose();
    });
  });
}

function updateCharacterUi(definition:RegisteredCharacter) {
  $('#brandMark').textContent = definition.label.slice(0, 1).toUpperCase();
  $('#brandName').textContent = definition.label.toUpperCase();
  document.title = definition.id === DEFAULT_CHARACTER_ID ? 'Mizuki Light Studio' : `${definition.label} · Mizuki Light Studio`;
  canvas.setAttribute('aria-label', `${definition.label} 角色 3D 浏览视口`);
}

function syncReferenceRenderModeUi() {
  document.querySelectorAll<HTMLButtonElement>('[data-render-mode]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.renderMode === referenceRenderMode));
  });
}

function setReferenceRenderMode(mode:ReferenceRenderMode, notify = true) {
  referenceRenderMode = mode;
  character?.traverse((node) => {
    if (!(node instanceof THREE.Mesh) || node.userData.characterOutline) return;
    const binding = node.userData.referenceMaterials as CharacterMaterialBinding | undefined;
    if (binding) node.material = binding[mode];
  });
  syncReferenceRenderModeUi();
  applyRenderSettings();
  if (notify) showToast(mode === 'clay' ? '已切换到灰模观察' : '已恢复完整材质');
}

function poseJointDefinition(jointId:PoseJointId) {
  return poseJointDefinitions.find((definition) => definition.id === jointId)!;
}

function syncPoseRotationUi() {
  const editor = $('#poseRotationEditor');
  const binding = selectedPoseJoint ? poseRig.get(selectedPoseJoint) : undefined;
  editor.hidden = !binding;
  if (!binding || !selectedPoseJoint) return;
  const offset = getPoseOffsetEuler(binding);
  const values = {
    x:THREE.MathUtils.radToDeg(offset.x),
    y:THREE.MathUtils.radToDeg(offset.y),
    z:THREE.MathUtils.radToDeg(offset.z),
  };
  $('#poseJointName').textContent = poseJointDefinition(selectedPoseJoint).label;
  (['x', 'y', 'z'] as RotationAxis[]).forEach((axis) => {
    const suffix = axis.toUpperCase();
    ($('#poseRotation' + suffix) as HTMLInputElement).value = String(values[axis]);
    $('#poseRotation' + suffix + 'Value').textContent = `${values[axis].toFixed(1)}°`;
  });
}

function syncPoseUi() {
  const available = activeCharacterId === DEFAULT_CHARACTER_ID && poseRig.size > 0;
  const poseModeButton = $('#poseModeBtn') as HTMLButtonElement;
  poseModeButton.disabled = !available;
  poseModeButton.setAttribute('aria-pressed', String(poseModeEnabled));
  poseModeButton.textContent = poseModeEnabled ? '退出姿势模式' : '进入姿势模式';
  ($('#resetPoseBtn') as HTMLButtonElement).disabled = !available;
  (['downloadPoseBtn', 'copyPoseBtn', 'sharePoseBtn'] as const).forEach((id) => {
    ($('#' + id) as HTMLButtonElement).disabled = !available;
  });
  $('#poseHint').textContent = !available
    ? '当前角色暂未配置姿势骨骼。'
    : poseModeEnabled
      ? '选择一个关节，在视口使用局部旋转环或下方滑块调整。'
      : '进入姿势模式后，可像关节人偶一样调整 Mizuki 全身与发丝。';
  document.querySelectorAll<HTMLButtonElement>('[data-pose-joint]').forEach((button) => {
    const jointId = button.dataset.poseJoint as PoseJointId;
    button.disabled = !available || !poseRig.has(jointId);
    button.setAttribute('aria-pressed', String(poseModeEnabled && selectedPoseJoint === jointId));
  });
  syncPoseRotationUi();
}

function setPoseMode(enabled:boolean, notify = true) {
  const canEnable = activeCharacterId === DEFAULT_CHARACTER_ID && poseRig.size > 0;
  poseModeEnabled = enabled && canEnable;
  selectedPoseJoint = null;
  transform.detach();
  transform.setMode(poseModeEnabled ? 'rotate' : 'translate');
  transform.setSpace(poseModeEnabled ? 'local' : 'world');
  if (poseModeEnabled) {
    selected = null;
    selectedReferenceId = null;
    renderReferenceList();
    renderLightList();
    syncEditor();
  }
  syncPoseUi();
  if (notify) showToast(poseModeEnabled ? '姿势模式已开启' : '姿势模式已关闭');
}

function selectPoseJoint(jointId:PoseJointId) {
  if (!poseModeEnabled) return;
  const binding = poseRig.get(jointId);
  if (!binding) return;
  selectedPoseJoint = jointId;
  transform.setMode('rotate');
  transform.setSpace('local');
  transform.attach(binding.bone);
  syncPoseUi();
}

function setupPoseRig(characterId:CharacterId, root:THREE.Object3D) {
  poseRig = characterId === DEFAULT_CHARACTER_ID ? bindPoseRig(root) : new Map();
  selectedPoseJoint = null;
  poseModeEnabled = false;
  transform.detach();
  transform.setMode('translate');
  transform.setSpace('world');
  if (selected) transform.attach(selected.light);
  const missing = poseJointDefinitions.filter((definition) => !poseRig.has(definition.id));
  if (characterId === DEFAULT_CHARACTER_ID && missing.length) {
    console.warn('[pose] 缺少骨骼映射:', missing.map((definition) => definition.boneName));
  }
  if (!sharedPoseApplied) {
    const encodedPose = new URL(location.href).searchParams.get('pose');
    if (encodedPose) {
      sharedPoseApplied = true;
      try { importPoseDocument(decodePoseUrl(encodedPose), false); }
      catch (error) { showToast(error instanceof Error ? error.message : '分享姿势读取失败'); }
    }
  }
  syncPoseUi();
}

function loadCharacter(characterId:CharacterId) {
  const definition = characterRegistry[characterId];
  if (character && characterId !== activeCharacterId) {
    undoStack.length = 0;
    redoStack.length = 0;
    endContinuousChange();
    syncUndoUi();
  }
  if (poseModeEnabled) setPoseMode(false, false);
  const loadToken = ++characterLoadToken;
  const modelSelect = $('#modelSelect') as HTMLSelectElement;
  const switchModelButton = $('#switchModelBtn') as HTMLButtonElement;
  modelSelect.disabled = true;
  switchModelButton.disabled = true;
  $('#loading').classList.remove('hidden');
  $('.viewport-status').classList.remove('ready');
  $('#statusText').textContent = `正在加载 ${definition.label}`;
  $('.loading-title').textContent = `正在构建 ${definition.label}`;
  setProgress(0);
  const manager = new THREE.LoadingManager();
  manager.onProgress = (_url, loaded, total) => setProgress(total ? loaded / total : .5);
  manager.onError = (url) => showToast(`资源加载失败：${url.split('/').pop()}`);
  const loader = new FBXLoader(manager);
  loader.load(definition.modelUrl, (object) => {
    if (loadToken !== characterLoadToken) {
      disposeCharacter(object);
      return;
    }
    const box = new THREE.Box3().setFromObject(object); const size = box.getSize(new THREE.Vector3());
    const scale = 3.65 / Math.max(size.y, .001); object.scale.setScalar(scale);
    box.setFromObject(object); const center = box.getCenter(new THREE.Vector3()); object.position.sub(center); object.position.y += box.getSize(new THREE.Vector3()).y / 2;
    object.updateMatrixWorld(true);
    clearRenderBindings();
    const outlineAdditions:Array<{ parent:THREE.Object3D; mesh:THREE.Mesh }> = [];
    const meshWorldScale = new THREE.Vector3();
    object.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      if (/^(Eff_001|Eff_002|EffectMesh)$/.test(node.name)) { node.visible = false; return; }
      node.castShadow = true; node.receiveShadow = !/^(Face|Face_Eye)$/.test(node.name);
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      const profiles = materials.map((material) => materialProfile(characterId, material.name || '', node.name));
      const rebuilt = materials.map((material) => rebuildMaterial(characterId, material, node.name));
      node.material = rebuilt.length === 1 ? rebuilt[0] : rebuilt;
      node.getWorldScale(meshWorldScale);
      const averageWorldScale = Math.max(
        .0001,
        (Math.abs(meshWorldScale.x) + Math.abs(meshWorldScale.y) + Math.abs(meshWorldScale.z)) / 3,
      );
      const outlineMaterials = profiles.map((profile, index) => {
        const style = outlineStyle(characterId, profile);
        const rebuiltMap = (rebuilt[index] as THREE.MeshBasicMaterial | THREE.MeshPhongMaterial).map;
        return createOutlineMaterial(
          characterId,
          profile,
          rebuiltMap,
          style ? style.worldWidth / averageWorldScale : 0,
        );
      });
      if (outlineMaterials.some((material) => material.visible)) {
        const outlineMesh = node.clone(false) as THREE.Mesh;
        outlineMesh.name = `${node.name}__outline`;
        outlineMesh.material = outlineMaterials.length === 1 ? outlineMaterials[0] : outlineMaterials;
        outlineMesh.castShadow = false;
        outlineMesh.receiveShadow = false;
        outlineMesh.renderOrder = -1;
        outlineMesh.userData.characterOutline = true;
        if (node.parent) outlineAdditions.push({ parent:node.parent, mesh:outlineMesh });
      } else {
        outlineMaterials.forEach((material) => material.dispose());
      }
      const fullMaterial = node.material;
      const clayMaterial = createClayMaterialSet(fullMaterial, `${characterId}-${node.name}`);
      node.userData.referenceMaterials = { full:fullMaterial, clay:clayMaterial } satisfies CharacterMaterialBinding;
      node.material = referenceRenderMode === 'clay' ? clayMaterial : fullMaterial;
      materials.forEach((material) => {
        const sourceMap = (material as THREE.MeshPhongMaterial).map;
        sourceMap?.dispose();
        material.dispose();
      });
    });
    outlineAdditions.forEach(({ parent, mesh }) => parent.add(mesh));
    applyRenderSettings();
    const previousCharacter = character;
    character = object;
    activeCharacterId = characterId;
    setupPoseRig(characterId, object);
    scene.add(object);
    if (previousCharacter) {
      scene.remove(previousCharacter);
      disposeCharacter(previousCharacter);
    }
    modelSelect.value = characterId;
    modelSelect.disabled = false;
    switchModelButton.disabled = false;
    updateCharacterUi(definition);
    resetView();
    setProgress(1);
    setTimeout(() => {
      if (loadToken !== characterLoadToken) return;
      $('#loading').classList.add('hidden');
      $('.viewport-status').classList.add('ready');
      $('#statusText').textContent = `${definition.label} 渲染就绪`;
      showToast(`已切换至 ${definition.label}`);
    }, 280);
  }, (event) => {
    if (loadToken === characterLoadToken && event.total) setProgress(event.loaded / event.total);
  }, (error) => {
    if (loadToken !== characterLoadToken) return;
    console.error(error);
    modelSelect.disabled = false;
    switchModelButton.disabled = false;
    modelSelect.value = activeCharacterId;
    $('#loading').classList.add('hidden');
    $('.viewport-status').classList.toggle('ready', Boolean(character));
    $('#statusText').textContent = character ? `${characterRegistry[activeCharacterId].label} 渲染就绪` : '模型加载失败';
    showToast(`无法加载 ${definition.label} FBX，请检查资源路径`);
  });
}
function setProgress(value:number) { const pct = Math.round(value*100); $('#progressBar').style.width = `${pct}%`; $('#progressText').textContent = `${pct}%`; }

type RotationAxis = 'x' | 'y' | 'z';
const directionalForward = new THREE.Vector3(0, 0, 1);
const directionalVector = new THREE.Vector3();
const directionalQuaternion = new THREE.Quaternion();

function syncDirectionalRotationFromPosition(item:ManagedLight) {
  if (!(item.light instanceof THREE.DirectionalLight)) return;
  directionalVector.copy(item.light.position).sub(item.light.target.position);
  item.directionDistance = Math.max(.1, directionalVector.length());
  directionalVector.normalize();
  directionalQuaternion.setFromUnitVectors(directionalForward, directionalVector);
  if (!item.directionRotation) item.directionRotation = new THREE.Euler(0, 0, 0, 'XYZ');
  item.directionRotation.setFromQuaternion(directionalQuaternion, 'XYZ');
}

function applyDirectionalRotation(item:ManagedLight) {
  if (!(item.light instanceof THREE.DirectionalLight) || !item.directionRotation) return;
  directionalVector.copy(directionalForward).applyEuler(item.directionRotation).normalize();
  item.light.position.copy(item.light.target.position).addScaledVector(directionalVector, item.directionDistance ?? 6);
  item.light.updateMatrixWorld();
  item.helper.update();
}

function rotationDegrees(value:number) {
  const degrees = THREE.MathUtils.radToDeg(value);
  return Math.abs(degrees) < .05 ? 0 : Math.round(degrees * 10) / 10;
}

function syncDirectionalRotationEditor(item:ManagedLight) {
  if (!(item.light instanceof THREE.DirectionalLight) || !item.directionRotation) return;
  (['x', 'y', 'z'] as RotationAxis[]).forEach((axis) => {
    const value = rotationDegrees(item.directionRotation![axis]);
    ($('#lightRotation' + axis.toUpperCase()) as HTMLInputElement).value = String(value);
    $('#lightRotation' + axis.toUpperCase() + 'Value').textContent = `${value.toFixed(1)}°`;
  });
}

function makeLight(kind:ManagedLight['kind'], name:string, color:string, intensity:number, position:THREE.Vector3) {
  const light = kind === 'point' ? new THREE.PointLight(color,intensity,14,2) : new THREE.DirectionalLight(color,intensity);
  light.position.copy(position); light.castShadow = true; light.shadow.mapSize.set(1024,1024); light.shadow.bias = -.0005;
  if (light instanceof THREE.DirectionalLight) { light.target.position.set(0,1.5,0); scene.add(light.target); light.shadow.camera.left=-4; light.shadow.camera.right=4; light.shadow.camera.top=5; light.shadow.camera.bottom=-2; }
  const helper = light instanceof THREE.PointLight ? new THREE.PointLightHelper(light,.13) : new THREE.DirectionalLightHelper(light,.45);
  lightGroup.add(light,helper); const item:ManagedLight={id:`light-${++lightIndex}`,name,kind,light,helper,enabled:true,baseIntensity:intensity};
  if (light instanceof THREE.DirectionalLight) {
    syncDirectionalRotationFromPosition(item);
    item.defaultDirectionRotation = item.directionRotation!.clone();
  }
  lights.push(item); renderLightList(); selectLight(item); return item;
}
function disposeLight(item:ManagedLight) { if (selected===item) { transform.detach(); selected=null; } lightGroup.remove(item.light,item.helper); if(item.light instanceof THREE.DirectionalLight) scene.remove(item.light.target); item.helper.dispose(); const i=lights.indexOf(item); if(i>=0)lights.splice(i,1); renderLightList(); selectLight(lights[0]??null); }
function selectLight(item:ManagedLight|null) { if(item&&poseModeEnabled)setPoseMode(false,false);if(item){selectedReferenceId=null;renderReferenceList();}selected=item;if(item){transform.setMode('translate');transform.setSpace('world');transform.attach(item.light);}else transform.detach();renderLightList();syncEditor(); }
function renderLightList() {
  $('#lightCount').textContent=`${lights.length} / 8`; const root=$('#lightList'); root.replaceChildren();
  for(const item of lights){ const row=document.createElement('div'); row.className=`light-item${selected===item?' selected':''}`; row.dataset.id=item.id;
    row.innerHTML=`<span class="light-swatch" style="background:#${item.light.color.getHexString()}"></span><span class="light-meta"><strong></strong><small>${item.kind==='point'?'点光源':'平行光'}</small></span><input class="light-enabled" type="checkbox" ${item.enabled?'checked':''} aria-label="开关 ${item.name}">`;
    row.querySelector('strong')!.textContent=item.name; row.addEventListener('click',(e)=>{ if((e.target as HTMLElement).classList.contains('light-enabled'))return;selectLight(item); });
    row.querySelector<HTMLInputElement>('input')!.addEventListener('change',(e)=>{ pushUndo('切换灯光'); item.enabled=(e.target as HTMLInputElement).checked; item.light.intensity=item.enabled?item.baseIntensity:0; item.helper.visible=item.enabled && ($('#showHelpers') as HTMLInputElement).checked; if(selected===item)syncEditor(); }); root.append(row);
  }
}
function syncEditor(){
  const disabled=!selected;
  $('#editorSection').classList.toggle('disabled',disabled);
  if(!selected)return;
  const l=selected.light;
  ($('#lightName') as HTMLInputElement).value=selected.name;
  ($('#lightColor') as HTMLInputElement).value=`#${l.color.getHexString()}`;
  ($('#lightIntensity') as HTMLInputElement).value=String(selected.baseIntensity);
  $('#lightIntensityValue').textContent=selected.baseIntensity.toFixed(1);
  const point=l instanceof THREE.PointLight;
  $('#distanceControl').style.display=point?'block':'none';
  $('#directionRotationControl').style.display=point?'none':'block';
  if(point){
    ($('#lightDistance') as HTMLInputElement).value=String(l.distance);
    $('#lightDistanceValue').textContent=l.distance.toFixed(1);
  } else {
    syncDirectionalRotationEditor(selected);
  }
}
function clearLights(){ [...lights].forEach(disposeLight); }
function applyPreset(preset:string){ clearLights(); if(preset==='warm'){ambient.intensity=.45;makeLight('directional','暖色主光','#ffb066',3.4,new THREE.Vector3(3.2,5,4));makeLight('point','暖色轮廓','#ff6757',2.2,new THREE.Vector3(-2.7,2.4,-2));}
  else if(preset==='rim'){ambient.intensity=.3;makeLight('directional','冷色主光','#8eacff',3.1,new THREE.Vector3(3.8,4.8,3));makeLight('point','青色轮廓','#55dff5',3.6,new THREE.Vector3(-2.5,3,-2.2));}
  else{ambient.intensity=.65;makeLight('directional','主光','#fff1dd',3,new THREE.Vector3(3.8,5.2,4));makeLight('point','补光','#91b8ff',1.8,new THREE.Vector3(-3,2.3,2.2));}
  if (preset === 'studio') {
    const initialDirectional = lights.find((item) => item.kind === 'directional');
    if (initialDirectional) {
      initialDirectional.baseIntensity = .7;
      initialDirectional.light.intensity = initialDirectional.enabled ? .7 : 0;
    }
  }
  ambient.intensity = preset === 'warm' ? .58 : preset === 'rim' ? .48 : .72;
  ($('#ambientIntensity') as HTMLInputElement).value=String(ambient.intensity);$('#ambientValue').textContent=ambient.intensity.toFixed(2); document.querySelectorAll('.preset').forEach(x=>x.classList.toggle('active',(x as HTMLElement).dataset.preset===preset));
}
function resetView(){camera.position.set(4.4,3.15,7.6);controls.target.set(0,1.75,0);controls.update();}
function resetStudio() {
  controls.autoRotate = false;
  $('#autoRotateBtn').setAttribute('aria-pressed', 'false');
  setFocusMode(false);
  resetPoseRig(poseRig);
  selectedReferenceId = null;
  referenceManager.clear();
  renderReferenceList();
  setPoseMode(false, false);
  resetView();
  applyPreset('studio');
  setReferenceRenderMode('full', false);
  Object.assign(renderSettings, defaultRenderSettings);
  syncRenderControlUi();
  applyRenderSettings();
  const backgroundInput = $('#backgroundColor') as HTMLInputElement;
  backgroundInput.value = DEFAULT_BACKGROUND;
  usesDefaultBackground = true;
  currentBackgroundColor = DEFAULT_BACKGROUND;
  scene.background = defaultBackgroundTexture;
  scene.fog = new THREE.Fog(DEFAULT_FOG, 16, 32);
  const showGrid = $('#showGrid') as HTMLInputElement;
  const showHelpers = $('#showHelpers') as HTMLInputElement;
  const enableShadows = $('#enableShadows') as HTMLInputElement;
  showGrid.checked = false;
  showHelpers.checked = true;
  enableShadows.checked = true;
  grid.visible = false;
  renderer.shadowMap.enabled = true;
  lights.forEach((item) => { item.helper.visible = item.enabled; });
  showToast('已恢复 Mizuki Light Studio 默认状态');
}
type CameraPresetId = 'full' | 'portrait' | 'face' | 'front';
const cameraPresets:Record<CameraPresetId, { position:THREE.Vector3; target:THREE.Vector3 }> = {
  full:{ position:new THREE.Vector3(4.4, 3.15, 7.6), target:new THREE.Vector3(0, 1.75, 0) },
  portrait:{ position:new THREE.Vector3(2.8, 2.75, 5), target:new THREE.Vector3(0, 2.05, 0) },
  face:{ position:new THREE.Vector3(1.25, 2.85, 2.9), target:new THREE.Vector3(0, 2.58, 0) },
  front:{ position:new THREE.Vector3(0, 2.3, 6.3), target:new THREE.Vector3(0, 1.95, 0) },
};
function applyCameraPreset(presetId:CameraPresetId) {
  const preset = cameraPresets[presetId];
  camera.position.copy(preset.position);
  controls.target.copy(preset.target);
  controls.update();
  showToast(`已切换到${presetId === 'full' ? '全身' : presetId === 'portrait' ? '半身' : presetId === 'face' ? '面部' : '正面'}机位`);
}
type RenderControlDefinition = {
  inputId:string;
  outputId:string;
  key:keyof typeof renderSettings;
  digits:number;
  label:string;
};
const renderControlDefinitions:RenderControlDefinition[] = [
  { inputId:'renderSaturation', outputId:'renderSaturationValue', key:'saturationScale', digits:2, label:'整体饱和度' },
  { inputId:'surfaceEmission', outputId:'surfaceEmissionValue', key:'surfaceEmission', digits:2, label:'全身自发光' },
  { inputId:'eyeEmission', outputId:'eyeEmissionValue', key:'eyeEmission', digits:2, label:'眼睛自发光' },
  { inputId:'faceBrightness', outputId:'faceBrightnessValue', key:'faceBrightness', digits:2, label:'面部亮度' },
  { inputId:'rampCenter', outputId:'rampCenterValue', key:'rampCenter', digits:3, label:'Ramp 阴影范围' },
  { inputId:'rampSoftness', outputId:'rampSoftnessValue', key:'rampSoftness', digits:3, label:'Ramp 过渡宽度' },
  { inputId:'outlineScale', outputId:'outlineScaleValue', key:'outlineScale', digits:2, label:'轮廓宽度' },
  { inputId:'bloomStrength', outputId:'bloomStrengthValue', key:'bloomStrength', digits:2, label:'泛光强度' },
  { inputId:'faceRimStrength', outputId:'faceRimStrengthValue', key:'faceRimStrength', digits:2, label:'背光脸颊边缘' },
];
function syncRenderControlUi() {
  renderControlDefinitions.forEach(({ inputId, outputId, key, digits }) => {
    const value = renderSettings[key];
    ($('#' + inputId) as HTMLInputElement).value = String(value);
    $('#' + outputId).textContent = value.toFixed(digits);
  });
}
function initializeRenderControls() {
  $('#renderStyleSection .section-title h2').textContent = '渲染风格';
  $('#resetRenderBtn').textContent = '恢复默认';
  $('#photoSection .section-title h2').textContent = '摄影工具';
  const cameraLabels:Record<CameraPresetId, string> = { full:'全身', portrait:'半身', face:'面部', front:'正面' };
  document.querySelectorAll<HTMLButtonElement>('[data-camera-preset]').forEach((button) => {
    button.textContent = cameraLabels[button.dataset.cameraPreset as CameraPresetId];
  });
  $('#focusModeBtn').textContent = '隐藏界面（H）';
  $('#capturePanelBtn').textContent = '导出截图';
  renderControlDefinitions.forEach(({ inputId, outputId, key, digits, label }) => {
    const input = $('#' + inputId) as HTMLInputElement;
    input.previousElementSibling?.querySelector('span')?.replaceChildren(label);
    input.addEventListener('input', (event) => {
      recordContinuousChange(label);
      const value = Number((event.target as HTMLInputElement).value);
      Object.assign(renderSettings, { [key]:value });
      $('#' + outputId).textContent = value.toFixed(digits);
      applyRenderSettings();
    });
  });
  syncRenderControlUi();
}
function setFocusMode(active:boolean) {
  if (active) { setPanelOpen(false); setReferencePanelOpen(false); }
  document.body.classList.toggle('focus-mode', active);
  const button = $('#focusModeBtn') as HTMLButtonElement;
  button.setAttribute('aria-pressed', String(active));
  button.textContent = active ? '显示界面（H）' : '隐藏界面（H）';
}
function showToast(message:string){const el=$('#toast');el.textContent=message;el.classList.add('show');setTimeout(()=>el.classList.remove('show'),2200);}
function addLight(kind:ManagedLight['kind']){if(lights.length>=8){showToast('最多可添加 8 盏光源');return;}makeLight(kind,kind==='point'?`点光 ${lights.length+1}`:`平行光 ${lights.length+1}`,'#ffffff',kind==='point'?2:2.5,new THREE.Vector3(2.5,3.5,3));}

let referenceTransformMode:'translate'|'rotate'|'scale' = 'translate';
const referenceParameterVisibility:Record<ReferenceObjectType, (keyof ReferenceObjectParameters)[]> = {
  box:['width','height','depth'], sphere:['radius','segments'], cylinder:['radius','height','segments'],
  triangularPrism:['width','height','depth'], stairs:['width','height','depth','steps'],
};

function renderReferenceList() {
  $('#referenceCount').textContent=`${referenceManager.records.size} / ${referenceManager.limits.maxObjects}`;
  const full=!referenceManager.canCreate();
  document.querySelectorAll<HTMLButtonElement>('[data-add-reference]').forEach((button)=>button.disabled=full);
  const list=$('#referenceList'); list.replaceChildren();
  referenceManager.records.forEach((record)=>{
    const button=document.createElement('button'); button.type='button'; button.dataset.referenceId=record.id;
    button.setAttribute('aria-pressed',String(record.id===selectedReferenceId));
    const name=document.createElement('span'); name.textContent=record.name;
    const type=document.createElement('small'); type.textContent=referenceObjectLabels[record.type];
    button.append(name,type); button.addEventListener('click',()=>selectReference(record.id)); list.append(button);
  });
  syncReferenceEditor();
}

function setReferenceTool(mode:typeof referenceTransformMode) {
  referenceTransformMode=mode;
  document.querySelectorAll<HTMLButtonElement>('[data-reference-tool]').forEach((button)=>button.setAttribute('aria-pressed',String(button.dataset.referenceTool===mode)));
  if(selectedReferenceId){ transform.setMode(mode); transform.setSpace(mode==='translate'?'world':'local'); }
}

function selectReference(id:ReferenceObjectId|null) {
  const record=referenceManager.get(id);
  selectedReferenceId=record?.id??null;
  if(record){
    if(poseModeEnabled)setPoseMode(false,false);
    selected=null; renderLightList(); syncEditor();
    transform.attach(record.object); setReferenceTool(referenceTransformMode);
  } else if(!selectedPoseJoint&&!selected) transform.detach();
  renderReferenceList();
}

function syncReferenceEditor() {
  const record=referenceManager.get(selectedReferenceId);
  $('#referenceEditor').classList.toggle('disabled',!record);
  if(!record)return;
  ($('#referenceName') as HTMLInputElement).value=record.name;
  const visible=new Set(referenceParameterVisibility[record.type]);
  document.querySelectorAll<HTMLElement>('[data-reference-param]').forEach((row)=>row.hidden=!visible.has(row.dataset.referenceParam as keyof ReferenceObjectParameters));
  (['width','height','depth','radius','segments','steps'] as const).forEach((key)=>{ const input=$('#reference'+key[0].toUpperCase()+key.slice(1)) as HTMLInputElement; input.value=String(record.parameters[key]); });
  const material=record.object.material as THREE.MeshStandardMaterial;
  ($('#referenceColor') as HTMLInputElement).value=`#${material.color.getHexString()}`;
  ($('#referenceOpacity') as HTMLInputElement).value=String(material.opacity);
  ($('#referenceVisible') as HTMLInputElement).checked=record.object.visible;
  ($('#referenceWireframe') as HTMLInputElement).checked=material.wireframe;
  ($('#referenceShadow') as HTMLInputElement).checked=record.object.castShadow&&record.object.receiveShadow;
}

function addReference(type:ReferenceObjectType) {
  if(!referenceManager.canCreate()){showToast(`参考物已达到当前上限 ${referenceManager.limits.maxObjects}`);return;}
  pushUndo(`添加${referenceObjectLabels[type]}`);
  const record=referenceManager.create(type); if(!record)return;
  renderReferenceList(); selectReference(record.id); showToast(`已添加${referenceObjectLabels[type]}`);
}

function removeSelectedReference() {
  if(!selectedReferenceId)return;
  pushUndo('删除参考物'); const id=selectedReferenceId; selectedReferenceId=null; transform.detach(); referenceManager.remove(id); renderReferenceList(); showToast('参考物已删除');
}

function duplicateSelectedReference() {
  if(!selectedReferenceId)return;
  if(!referenceManager.canCreate()){showToast(`参考物已达到当前上限 ${referenceManager.limits.maxObjects}`);return;}
  pushUndo('复制参考物'); const copy=referenceManager.duplicate(selectedReferenceId); if(copy){renderReferenceList();selectReference(copy.id);}
}

function resetSelectedReference() {
  const record=referenceManager.get(selectedReferenceId); if(!record)return;
  pushUndo('复位参考物'); record.object.position.set(0,0,0);record.object.rotation.set(0,0,0);record.object.scale.set(1,1,1);syncReferenceEditor();
}

function setReferencePanelOpen(open:boolean) {
  if(open)setPanelOpen(false);
  const panel=$('#referencePanel'); panel.hidden=!open;
  $('#referencePanelBtn').setAttribute('aria-pressed',String(open));
  document.body.classList.toggle('reference-panel-open',open);
}

type QuaternionTuple = [number, number, number, number];
type VectorTuple = [number, number, number];
type PoseDocument = {
  format:'mizuki-pose';
  version:1;
  character:CharacterId;
  name:string;
  createdAt:string;
  joints:Partial<Record<PoseJointId, QuaternionTuple>>;
};
type LightSnapshot = {
  kind:ManagedLight['kind']; name:string; color:string; intensity:number; enabled:boolean;
  position:VectorTuple; distance?:number; rotation?:VectorTuple; defaultRotation?:VectorTuple;
};
type StudioSnapshot = {
  pose:Partial<Record<PoseJointId, QuaternionTuple>>;
  backgroundColor:string; usesDefaultBackground:boolean; ambientIntensity:number;
  showGrid:boolean; showHelpers:boolean; enableShadows:boolean;
  renderMode:ReferenceRenderMode; renderSettings:typeof renderSettings;
  lights:LightSnapshot[]; selectedLightIndex:number;
  referenceObjects:ReferenceObjectData[]; selectedReferenceId:ReferenceObjectId|null;
};
type UndoEntry = { label:string; snapshot:StudioSnapshot };
const undoStack:UndoEntry[] = [];
const redoStack:UndoEntry[] = [];
const MAX_UNDO_STEPS = 50;
let continuousChangeLabel:string | null = null;
let restoringSnapshot = false;

function quaternionTuple(quaternion:THREE.Quaternion):QuaternionTuple {
  return [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
}

function capturePose() {
  const joints:Partial<Record<PoseJointId, QuaternionTuple>> = {};
  poseJointDefinitions.forEach(({ id }) => {
    const binding = poseRig.get(id);
    if (binding) joints[id] = quaternionTuple(getPoseOffsetQuaternion(binding));
  });
  return joints;
}

function captureStudioSnapshot():StudioSnapshot {
  return {
    pose:capturePose(),
    backgroundColor:currentBackgroundColor,
    usesDefaultBackground,
    ambientIntensity:ambient.intensity,
    showGrid:($('#showGrid') as HTMLInputElement).checked,
    showHelpers:($('#showHelpers') as HTMLInputElement).checked,
    enableShadows:renderer.shadowMap.enabled,
    renderMode:referenceRenderMode,
    renderSettings:{ ...renderSettings },
    lights:lights.map((item) => ({
      kind:item.kind,
      name:item.name,
      color:`#${item.light.color.getHexString()}`,
      intensity:item.baseIntensity,
      enabled:item.enabled,
      position:item.light.position.toArray() as VectorTuple,
      distance:item.light instanceof THREE.PointLight ? item.light.distance : undefined,
      rotation:item.directionRotation?.toArray().slice(0, 3) as VectorTuple | undefined,
      defaultRotation:item.defaultDirectionRotation?.toArray().slice(0, 3) as VectorTuple | undefined,
    })),
    selectedLightIndex:selected ? lights.indexOf(selected) : -1,
    referenceObjects:referenceManager.serialize(),
    selectedReferenceId,
  };
}

function syncUndoUi() {
  const undoButton = $('#undoBtn') as HTMLButtonElement;
  const redoButton = $('#redoBtn') as HTMLButtonElement;
  undoButton.disabled = undoStack.length === 0;
  undoButton.title = undoStack.length ? `撤销：${undoStack.at(-1)!.label}（Ctrl+Z）` : '没有可撤销的操作';
  redoButton.disabled = redoStack.length === 0;
  redoButton.title = redoStack.length ? `恢复：${redoStack.at(-1)!.label}（Ctrl+Shift+Z）` : '没有可恢复的操作';
}

function pushUndo(label:string) {
  if (restoringSnapshot || !character) return;
  undoStack.push({ label, snapshot:captureStudioSnapshot() });
  if (undoStack.length > MAX_UNDO_STEPS) undoStack.shift();
  redoStack.length = 0;
  syncUndoUi();
}

function recordContinuousChange(label:string) {
  if (continuousChangeLabel === label) return;
  endContinuousChange();
  continuousChangeLabel = label;
  pushUndo(label);
}

function endContinuousChange() { continuousChangeLabel = null; }

function applyPoseJoints(joints:Partial<Record<PoseJointId, QuaternionTuple>>) {
  let applied = 0;
  Object.entries(joints).forEach(([id, tuple]) => {
    if (!tuple || tuple.length !== 4 || !tuple.every(Number.isFinite)) return;
    const binding = poseRig.get(id as PoseJointId);
    if (!binding) return;
    setPoseOffsetQuaternion(binding, new THREE.Quaternion(...tuple));
    applied += 1;
  });
  syncPoseRotationUi();
  return applied;
}

function applyLightSnapshot(item:ManagedLight, state:LightSnapshot) {
  item.name = state.name;
  item.light.color.set(state.color);
  item.baseIntensity = state.intensity;
  item.enabled = state.enabled;
  item.light.intensity = state.enabled ? state.intensity : 0;
  item.light.position.fromArray(state.position);
  if (item.light instanceof THREE.PointLight && state.distance !== undefined) item.light.distance = state.distance;
  if (item.directionRotation && state.rotation) item.directionRotation.fromArray(state.rotation);
  if (item.defaultDirectionRotation && state.defaultRotation) item.defaultDirectionRotation.fromArray(state.defaultRotation);
  if (item.light instanceof THREE.DirectionalLight && item.directionRotation) applyDirectionalRotation(item);
  item.helper.visible = state.enabled && ($('#showHelpers') as HTMLInputElement).checked;
  item.helper.update();
}

function restoreLights(states:LightSnapshot[], selectedIndex:number) {
  const canReuse = lights.length === states.length && lights.every((item, index) => item.kind === states[index].kind);
  if (!canReuse) {
    clearLights();
    states.forEach((state) => {
      const item = makeLight(state.kind, state.name, state.color, state.intensity, new THREE.Vector3(...state.position));
      applyLightSnapshot(item, state);
    });
  } else lights.forEach((item, index) => applyLightSnapshot(item, states[index]));
  selectLight(selectedIndex >= 0 ? lights[selectedIndex] ?? null : null);
  renderLightList();
  syncEditor();
}

function restoreStudioSnapshot(snapshot:StudioSnapshot) {
  restoringSnapshot = true;
  const wasPoseMode = poseModeEnabled;
  const poseJoint = selectedPoseJoint;
  applyPoseJoints(snapshot.pose);
  usesDefaultBackground = snapshot.usesDefaultBackground;
  currentBackgroundColor = snapshot.backgroundColor;
  ($('#backgroundColor') as HTMLInputElement).value = snapshot.backgroundColor;
  scene.background = usesDefaultBackground ? defaultBackgroundTexture : new THREE.Color(snapshot.backgroundColor);
  scene.fog = new THREE.Fog(usesDefaultBackground ? DEFAULT_FOG : snapshot.backgroundColor, 16, 32);
  ambient.intensity = snapshot.ambientIntensity;
  ($('#ambientIntensity') as HTMLInputElement).value = String(ambient.intensity);
  $('#ambientValue').textContent = ambient.intensity.toFixed(2);
  ($('#showGrid') as HTMLInputElement).checked = snapshot.showGrid;
  ($('#showHelpers') as HTMLInputElement).checked = snapshot.showHelpers;
  ($('#enableShadows') as HTMLInputElement).checked = snapshot.enableShadows;
  grid.visible = snapshot.showGrid;
  renderer.shadowMap.enabled = snapshot.enableShadows;
  setReferenceRenderMode(snapshot.renderMode, false);
  Object.assign(renderSettings, snapshot.renderSettings);
  syncRenderControlUi();
  applyRenderSettings();
  restoreLights(snapshot.lights, snapshot.selectedLightIndex);
  referenceManager.deserialize(snapshot.referenceObjects);
  renderReferenceList();
  selectReference(snapshot.selectedReferenceId && referenceManager.get(snapshot.selectedReferenceId) ? snapshot.selectedReferenceId : null);
  if (wasPoseMode) {
    setPoseMode(true, false);
    if (poseJoint && poseRig.has(poseJoint)) selectPoseJoint(poseJoint);
  }
  restoringSnapshot = false;
}

function undoLastChange() {
  const entry = undoStack.pop();
  if (!entry) return;
  endContinuousChange();
  redoStack.push({ label:entry.label, snapshot:captureStudioSnapshot() });
  if (redoStack.length > MAX_UNDO_STEPS) redoStack.shift();
  restoreStudioSnapshot(entry.snapshot);
  syncUndoUi();
  showToast(`已撤销：${entry.label}`);
}

function redoLastChange() {
  const entry = redoStack.pop();
  if (!entry) return;
  endContinuousChange();
  undoStack.push({ label:entry.label, snapshot:captureStudioSnapshot() });
  if (undoStack.length > MAX_UNDO_STEPS) undoStack.shift();
  restoreStudioSnapshot(entry.snapshot);
  syncUndoUi();
  showToast(`已恢复：${entry.label}`);
}

function createPoseDocument():PoseDocument {
  const joints:PoseDocument['joints'] = {};
  Object.entries(capturePose()).forEach(([id, tuple]) => {
    if (!tuple) return;
    const changed = Math.abs(tuple[0]) > 1e-5 || Math.abs(tuple[1]) > 1e-5 || Math.abs(tuple[2]) > 1e-5 || Math.abs(tuple[3] - 1) > 1e-5;
    if (changed) joints[id as PoseJointId] = tuple;
  });
  return { format:'mizuki-pose', version:1, character:activeCharacterId, name:'未命名姿势', createdAt:new Date().toISOString(), joints };
}

function parsePoseDocument(value:unknown):PoseDocument {
  if (!value || typeof value !== 'object') throw new Error('姿势数据不是有效对象');
  const document = value as Partial<PoseDocument>;
  if (document.format !== 'mizuki-pose' || document.version !== 1 || !document.joints || typeof document.joints !== 'object') throw new Error('不支持的姿势文件格式');
  return document as PoseDocument;
}

function importPoseDocument(document:PoseDocument, record = true) {
  if (document.character !== activeCharacterId) throw new Error(`该姿势属于 ${document.character}，当前角色是 ${activeCharacterId}`);
  if (record) pushUndo('导入姿势');
  resetPoseRig(poseRig);
  const applied = applyPoseJoints(document.joints);
  showToast(`已导入姿势，共应用 ${applied} 个关节`);
}

function encodePoseUrl(document:PoseDocument) {
  const bytes = new TextEncoder().encode(JSON.stringify(document));
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).split('+').join('-').split('/').join('_').replace(/=+$/g, '');
}

function decodePoseUrl(value:string) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(padded);
  return parsePoseDocument(JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)))));
}

async function copyText(value:string, successMessage:string) {
  await navigator.clipboard.writeText(value);
  showToast(successMessage);
}
const faceLightDirection = new THREE.Vector3(0, 0, 1);
const faceDirectColor = new THREE.Color(0, 0, 0);
const faceAmbientColor = new THREE.Color(0, 0, 0);
const characterWorldRotation = new THREE.Quaternion();
function updateFaceLight() {
  const main = lights
    .filter((item) => item.enabled && item.light instanceof THREE.DirectionalLight)
    .sort((a, b) => b.baseIntensity - a.baseIntensity)[0];
  if (main?.light instanceof THREE.DirectionalLight) {
    faceLightDirection.copy(main.light.position).sub(main.light.target.position).normalize();
    faceDirectColor.copy(main.light.color).multiplyScalar(main.light.intensity);
    if (character) {
      character.getWorldQuaternion(characterWorldRotation);
      faceLightDirection.applyQuaternion(characterWorldRotation.invert()).normalize();
    }
  } else {
    faceLightDirection.set(0, 0, 1);
    faceDirectColor.setRGB(0, 0, 0);
  }
  // A stable virtual face normal avoids exposing split FBX normals while matching
  // Three.js' hemispheric Lambert irradiance scale.
  faceAmbientColor
    .copy(ambient.groundColor)
    .lerp(ambient.color, .5)
    .multiplyScalar(ambient.intensity);
  faceLightUniforms.forEach((uniforms) => {
    uniforms.direction.value.copy(faceLightDirection);
    uniforms.directColor.value.copy(faceDirectColor);
    uniforms.ambientColor.value.copy(faceAmbientColor);
  });
}

poseJointDefinitions.forEach((definition) => {
  const group = document.querySelector<HTMLElement>(`[data-pose-group="${definition.group}"]`);
  if (!group) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.dataset.poseJoint = definition.id;
  button.textContent = definition.label;
  button.setAttribute('aria-pressed', 'false');
  group.append(button);
});

$('#resetViewBtn').onclick=resetView;
$('#resetStudioBtn').onclick=()=>{ pushUndo('全部重置'); resetStudio(); };
$('#resetLightsBtn').onclick=()=>{ pushUndo('恢复默认灯光'); applyPreset('studio'); };
$('#addPointBtn').onclick=()=>{ if(lights.length<8)pushUndo('添加点光'); addLight('point'); };
$('#addDirectionalBtn').onclick=()=>{ if(lights.length<8)pushUndo('添加平行光'); addLight('directional'); };
$('#deleteLightBtn').onclick=()=>{ if(selected){pushUndo('删除灯光');disposeLight(selected);} };
$('#undoBtn').addEventListener('click', undoLastChange);
$('#redoBtn').addEventListener('click', redoLastChange);
$('#referencePanelBtn').addEventListener('click',()=>setReferencePanelOpen($('#referencePanel').hidden));
$('#referencePanelClose').addEventListener('click',()=>setReferencePanelOpen(false));
document.querySelectorAll<HTMLButtonElement>('[data-add-reference]').forEach((button)=>button.addEventListener('click',()=>addReference(button.dataset.addReference as ReferenceObjectType)));
document.querySelectorAll<HTMLButtonElement>('[data-reference-tool]').forEach((button)=>button.addEventListener('click',()=>setReferenceTool(button.dataset.referenceTool as typeof referenceTransformMode)));
$('#duplicateReferenceBtn').addEventListener('click',duplicateSelectedReference);
$('#resetReferenceBtn').addEventListener('click',resetSelectedReference);
$('#deleteReferenceBtn').addEventListener('click',removeSelectedReference);
$('#referenceName').addEventListener('input',(event)=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;recordContinuousChange('重命名参考物');record.name=(event.target as HTMLInputElement).value;renderReferenceList();});
(['width','height','depth','radius','segments','steps'] as const).forEach((key)=>{
  const input=$('#reference'+key[0].toUpperCase()+key.slice(1)) as HTMLInputElement;
  input.addEventListener('input',()=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;recordContinuousChange('调整参考物尺寸');referenceManager.updateGeometry(record.id,{[key]:Number(input.value)});syncReferenceEditor();});
});
$('#referenceColor').addEventListener('input',(event)=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;recordContinuousChange('调整参考物颜色');(record.object.material as THREE.MeshStandardMaterial).color.set((event.target as HTMLInputElement).value);});
$('#referenceOpacity').addEventListener('input',(event)=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;recordContinuousChange('调整参考物透明度');const material=record.object.material as THREE.MeshStandardMaterial;material.opacity=Number((event.target as HTMLInputElement).value);material.transparent=material.opacity<1;material.needsUpdate=true;});
$('#referenceVisible').addEventListener('change',(event)=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;pushUndo('切换参考物显示');record.object.visible=(event.target as HTMLInputElement).checked;});
$('#referenceWireframe').addEventListener('change',(event)=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;pushUndo('切换参考物线框');(record.object.material as THREE.MeshStandardMaterial).wireframe=(event.target as HTMLInputElement).checked;});
$('#referenceShadow').addEventListener('change',(event)=>{const record=referenceManager.get(selectedReferenceId);if(!record)return;pushUndo('切换参考物阴影');const enabled=(event.target as HTMLInputElement).checked;record.object.castShadow=enabled;record.object.receiveShadow=enabled;});

const referenceRaycaster=new THREE.Raycaster();
const referencePointer=new THREE.Vector2();
canvas.addEventListener('dblclick',(event)=>{
  const rect=canvas.getBoundingClientRect();referencePointer.set((event.clientX-rect.left)/rect.width*2-1,-((event.clientY-rect.top)/rect.height*2-1));referenceRaycaster.setFromCamera(referencePointer,camera);
  const hit=referenceRaycaster.intersectObjects(referenceGroup.children,false)[0];
  if(hit){const id=hit.object.userData.referenceObjectId as ReferenceObjectId|undefined;if(id){setReferencePanelOpen(true);selectReference(id);}}
});

const referencePanel=$('#referencePanel');
const storedReferencePanelPosition=localStorage.getItem('mizuki-reference-panel-position');
if(storedReferencePanelPosition){try{const position=JSON.parse(storedReferencePanelPosition) as {left:number;top:number};referencePanel.style.left=`${position.left}px`;referencePanel.style.top=`${position.top}px`;}catch{/* Ignore invalid saved UI state. */}}
$('#referencePanelDrag').addEventListener('pointerdown',(event)=>{
  if(mobilePanelMedia.matches||(event.target as HTMLElement).closest('button'))return;
  const start=referencePanel.getBoundingClientRect();const offsetX=event.clientX-start.left,offsetY=event.clientY-start.top;
  const move=(moveEvent:PointerEvent)=>{const maxLeft=Math.max(8,innerWidth-referencePanel.offsetWidth-8),maxTop=Math.max(8,innerHeight-referencePanel.offsetHeight-8);referencePanel.style.left=`${THREE.MathUtils.clamp(moveEvent.clientX-offsetX,8,maxLeft)}px`;referencePanel.style.top=`${THREE.MathUtils.clamp(moveEvent.clientY-offsetY,8,maxTop)}px`;};
  const finish=()=>{document.removeEventListener('pointermove',move);document.removeEventListener('pointerup',finish);localStorage.setItem('mizuki-reference-panel-position',JSON.stringify({left:referencePanel.offsetLeft,top:referencePanel.offsetTop}));};
  document.addEventListener('pointermove',move);document.addEventListener('pointerup',finish);
});
const modelSelect = $('#modelSelect') as HTMLSelectElement;
characterList.forEach((definition) => modelSelect.add(new Option(definition.label, definition.id)));
$('#modelSelect').addEventListener('change',(event)=>{
  const nextId=(event.target as HTMLSelectElement).value as CharacterId;
  if(nextId!==activeCharacterId)loadCharacter(nextId);
});
$('#switchModelBtn').addEventListener('click',()=>{
  const currentIndex = characterList.findIndex(({ id }) => id === activeCharacterId);
  loadCharacter(characterList[(currentIndex + 1) % characterList.length].id);
});
document.querySelectorAll<HTMLElement>('.preset').forEach(btn=>btn.onclick=()=>{pushUndo('切换灯光预设');applyPreset(btn.dataset.preset!);});
document.querySelectorAll<HTMLButtonElement>('[data-render-mode]').forEach((button) => {
  button.addEventListener('click', () => { pushUndo('切换渲染模式'); setReferenceRenderMode(button.dataset.renderMode as ReferenceRenderMode); });
});
$('#poseModeBtn').addEventListener('click', () => setPoseMode(!poseModeEnabled));
document.querySelectorAll<HTMLButtonElement>('[data-pose-joint]').forEach((button) => {
  button.addEventListener('click', () => selectPoseJoint(button.dataset.poseJoint as PoseJointId));
});
(['x', 'y', 'z'] as RotationAxis[]).forEach((axis) => {
  $('#poseRotation' + axis.toUpperCase()).addEventListener('input', () => {
    const binding = selectedPoseJoint ? poseRig.get(selectedPoseJoint) : undefined;
    if (!binding) return;
    recordContinuousChange(`调整${poseJointDefinition(selectedPoseJoint!).label}`);
    const degrees = (['x', 'y', 'z'] as RotationAxis[]).map((item) =>
      Number(($('#poseRotation' + item.toUpperCase()) as HTMLInputElement).value),
    );
    setPoseOffsetEuler(binding, new THREE.Euler(
      THREE.MathUtils.degToRad(degrees[0]),
      THREE.MathUtils.degToRad(degrees[1]),
      THREE.MathUtils.degToRad(degrees[2]),
      'XYZ',
    ));
    syncPoseRotationUi();
  });
});
$('#resetPoseJointBtn').addEventListener('click', () => {
  const binding = selectedPoseJoint ? poseRig.get(selectedPoseJoint) : undefined;
  if (!binding) return;
  pushUndo('重置关节');
  resetPoseJoint(binding);
  syncPoseRotationUi();
  showToast('当前关节已重置');
});
$('#resetPoseBtn').addEventListener('click', () => {
  pushUndo('重置全部姿势');
  resetPoseRig(poseRig);
  syncPoseRotationUi();
  showToast('Mizuki 姿势已重置');
});
$('#backgroundColor').addEventListener('input',(e)=>{recordContinuousChange('调整背景色');const color=(e.target as HTMLInputElement).value;usesDefaultBackground=false;currentBackgroundColor=color;scene.background=new THREE.Color(color);scene.fog=new THREE.Fog(color,16,32);});
$('#ambientIntensity').addEventListener('input',(e)=>{recordContinuousChange('调整环境光');ambient.intensity=Number((e.target as HTMLInputElement).value);$('#ambientValue').textContent=ambient.intensity.toFixed(2);});
$('#showGrid').addEventListener('change',(e)=>{const input=e.target as HTMLInputElement;const next=input.checked;input.checked=!next;pushUndo('切换网格');input.checked=next;grid.visible=next;});
$('#showHelpers').addEventListener('change',(e)=>{const input=e.target as HTMLInputElement;const next=input.checked;input.checked=!next;pushUndo('切换灯光标记');input.checked=next;lights.forEach(x=>x.helper.visible=next&&x.enabled);});
$('#enableShadows').addEventListener('change',(e)=>{const input=e.target as HTMLInputElement;const next=input.checked;input.checked=!next;pushUndo('切换阴影');input.checked=next;renderer.shadowMap.enabled=next;});
$('#lightName').addEventListener('input',(e)=>{if(!selected)return;recordContinuousChange('修改灯光名称');selected.name=(e.target as HTMLInputElement).value;renderLightList();});
$('#lightColor').addEventListener('input',(e)=>{if(!selected)return;recordContinuousChange('调整灯光颜色');selected.light.color.set((e.target as HTMLInputElement).value);selected.helper.update();renderLightList();});
$('#lightIntensity').addEventListener('input',(e)=>{if(!selected)return;recordContinuousChange('调整灯光强度');selected.baseIntensity=Number((e.target as HTMLInputElement).value);selected.light.intensity=selected.enabled?selected.baseIntensity:0;$('#lightIntensityValue').textContent=selected.baseIntensity.toFixed(1);});
$('#lightDistance').addEventListener('input',(e)=>{if(!(selected?.light instanceof THREE.PointLight))return;recordContinuousChange('调整照射距离');selected.light.distance=Number((e.target as HTMLInputElement).value);$('#lightDistanceValue').textContent=selected.light.distance.toFixed(1);});
(['x', 'y', 'z'] as RotationAxis[]).forEach((axis) => {
  const suffix = axis.toUpperCase();
  $('#lightRotation' + suffix).addEventListener('input', (event) => {
    if (!(selected?.light instanceof THREE.DirectionalLight) || !selected.directionRotation) return;
    recordContinuousChange('调整平行光方向');
    selected.directionRotation[axis] = THREE.MathUtils.degToRad(Number((event.target as HTMLInputElement).value));
    applyDirectionalRotation(selected);
    syncDirectionalRotationEditor(selected);
  });
});
$('#resetLightRotationBtn').addEventListener('click', () => {
  if (!(selected?.light instanceof THREE.DirectionalLight) || !selected.directionRotation || !selected.defaultDirectionRotation) return;
  pushUndo('重置灯光方向');
  selected.directionRotation.copy(selected.defaultDirectionRotation);
  applyDirectionalRotation(selected);
  syncDirectionalRotationEditor(selected);
});
$('#autoRotateBtn').addEventListener('click',(e)=>{controls.autoRotate=!controls.autoRotate;controls.autoRotateSpeed=.8;(e.currentTarget as HTMLButtonElement).setAttribute('aria-pressed',String(controls.autoRotate));});
$('#captureBtn').onclick=()=>{const helper=transform.getHelper();const helperWasVisible=helper.visible;helper.visible=false;composer.render();const a=document.createElement('a');a.download=`${characterRegistry[activeCharacterId].slug}-${new Date().toISOString().slice(0,10)}.png`;a.href=renderer.domElement.toDataURL('image/png');a.click();helper.visible=helperWasVisible;showToast('画面已导出');};
initializeRenderControls();
$('#resetRenderBtn').addEventListener('click', () => {
  pushUndo('重置渲染参数');
  setReferenceRenderMode('full', false);
  Object.assign(renderSettings, defaultRenderSettings);
  syncRenderControlUi();
  applyRenderSettings();
  showToast('渲染参数已恢复默认');
});
document.querySelectorAll<HTMLButtonElement>('[data-camera-preset]').forEach((button) => {
  button.addEventListener('click', () => applyCameraPreset(button.dataset.cameraPreset as CameraPresetId));
});
$('#focusModeBtn').addEventListener('click', () => setFocusMode(!document.body.classList.contains('focus-mode')));
$('#capturePanelBtn').addEventListener('click', () => ($('#captureBtn') as HTMLButtonElement).click());
$('#downloadPoseBtn').addEventListener('click', () => {
  const poseDocument = createPoseDocument();
  const blobUrl = URL.createObjectURL(new Blob([JSON.stringify(poseDocument, null, 2)], { type:'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = `mizuki-pose-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
  anchor.click();
  URL.revokeObjectURL(blobUrl);
  showToast('姿势 JSON 已下载');
});
$('#copyPoseBtn').addEventListener('click', async () => {
  try { await copyText(JSON.stringify(createPoseDocument(), null, 2), '姿势 JSON 已复制'); }
  catch { showToast('复制失败，请改用下载 JSON'); }
});
$('#sharePoseBtn').addEventListener('click', async () => {
  const url = new URL(location.href);
  url.searchParams.set('pose', encodePoseUrl(createPoseDocument()));
  try { await copyText(url.toString(), '分享 URL 已复制'); }
  catch { showToast('复制失败，请改用 JSON'); }
});
$('#importPoseInput').addEventListener('change', async (event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  try { importPoseDocument(parsePoseDocument(JSON.parse(await file.text()))); }
  catch (error) { showToast(error instanceof Error ? error.message : '姿势导入失败'); }
  finally { input.value = ''; }
});
window.addEventListener('pointerup', endContinuousChange);
document.addEventListener('change', endContinuousChange);
window.addEventListener('keydown', (event) => {
  if(!$('#helpOverlay').hidden)return;
  const target = event.target as HTMLElement;
  const isTextEditing = /^(TEXTAREA|SELECT)$/.test(target.tagName) || (target instanceof HTMLInputElement && /^(text|color|file)$/i.test(target.type));
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase()==='d' && selectedReferenceId && !isTextEditing) { event.preventDefault(); duplicateSelectedReference(); return; }
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !isTextEditing) {
    event.preventDefault();
    if (event.shiftKey) redoLastChange();
    else undoLastChange();
    return;
  }
  if(selectedReferenceId&&!isTextEditing){
    if(event.key==='Delete'){event.preventDefault();removeSelectedReference();return;}
    const toolKeys:Record<string,typeof referenceTransformMode>={w:'translate',e:'rotate',r:'scale'};
    const tool=toolKeys[event.key.toLowerCase()];if(tool){event.preventDefault();setReferenceTool(tool);return;}
  }
  if (event.key.toLowerCase() === 'h' && !/^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) {
    setFocusMode(!document.body.classList.contains('focus-mode'));
  }
});
const controlPanel = $('#controlPanel');
const panelToggle = $('#panelToggle') as HTMLButtonElement;
const panelClose = $('#panelClose') as HTMLButtonElement;
const panelBackdrop = $('#panelBackdrop');
const mobilePanelMedia = window.matchMedia('(max-width: 800px)');
const widePanelMedia = window.matchMedia('(min-width: 1280px)');
function setPanelOpen(open:boolean) {
  if(open)setReferencePanelOpen(false);
  controlPanel.hidden=!open;
  controlPanel.classList.toggle('open',open);
  panelBackdrop.classList.remove('visible');
  document.body.classList.toggle('panel-open',open);
  document.body.classList.toggle('control-panel-open',open);
  panelToggle.setAttribute('aria-expanded',String(open));
  panelToggle.setAttribute('aria-label',open?'关闭控制台':'打开控制台');
  $('#consolePanelBtn').setAttribute('aria-pressed',String(open));
  panelBackdrop.setAttribute('aria-hidden','true');
  controlPanel.setAttribute('aria-hidden',String(!open));
  controlPanel.inert=!open;
  if(!open&&controlPanel.contains(document.activeElement))panelToggle.focus();
}
panelToggle.addEventListener('click', () => setPanelOpen(true));
$('#consolePanelBtn').addEventListener('click',()=>setPanelOpen(controlPanel.hidden));
panelClose.addEventListener('click', () => setPanelOpen(false));
panelBackdrop.addEventListener('click', () => setPanelOpen(false));
window.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && controlPanel.classList.contains('open')) setPanelOpen(false);
  else if(event.key==='Escape'&&!$('#referencePanel').hidden)setReferencePanelOpen(false);
});
mobilePanelMedia.addEventListener('change', () => setPanelOpen(false));
widePanelMedia.addEventListener('change',()=>setPanelOpen(false));
setPanelOpen(widePanelMedia.matches);
setupHelpCenter({ onOpen:()=>{setPanelOpen(false);setReferencePanelOpen(false);} });
window.addEventListener('resize',()=>{camera.aspect=innerWidth/innerHeight;camera.updateProjectionMatrix();renderer.setSize(innerWidth,innerHeight);renderer.setPixelRatio(Math.min(devicePixelRatio,2));composer.setSize(innerWidth,innerHeight);composer.setPixelRatio(Math.min(devicePixelRatio,2));});

renderReferenceList(); syncUndoUi(); applyPreset('studio'); loadCharacter(activeCharacterId);
const clock=new THREE.Clock(); renderer.setAnimationLoop(()=>{controls.update(clock.getDelta());updateFaceLight();composer.render();});
